import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Plane,
  Plus,
  AlertCircle,
  X,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  MapPin,
} from 'lucide-react'
import {
  getRemoteSensingSources,
  createRemoteSensingSource,
  getRemoteSensingObservations,
  bulkCreateObservations,
  getPaddocks,
} from '../../services/api'
import { format, parseISO } from 'date-fns'
import StatCard from '../../components/StatCard'

const AIRCRAFT = {
  mavic_enterprise: 'DJI Mavic Enterprise',
  matrice_300: 'DJI Matrice 300',
}

const SENSORS = {
  multispectral: 'Vegetation Sensor',
  rgb: 'RGB',
  lidar: 'LiDAR',
}

export default function DroneCalibration({ ranchId }) {
  const queryClient = useQueryClient()
  const [showFlightModal, setShowFlightModal] = useState(false)
  const [showObsModal, setShowObsModal] = useState(null) // source_id when open
  const [expandedFlight, setExpandedFlight] = useState(null)

  const { data: flights, isLoading } = useQuery({
    queryKey: ['droneSources', ranchId],
    queryFn: () => getRemoteSensingSources(ranchId, { source_type: 'drone' }),
  })

  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  const totalFlights = flights?.length || 0
  const latestFlight = flights?.[0]

  // Count unique paddocks across all flights (paddock_ids derived from observations server-side)
  const paddocksCovered = flights
    ? new Set(flights.flatMap(f => f.paddock_ids || [])).size
    : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Drone Calibration</h1>
            <p className="text-gray-500 mt-1">Log drone flights and enter processed observation data</p>
          </div>
        </div>
        <button
          onClick={() => setShowFlightModal(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Log New Flight
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Flights"
          value={totalFlights}
          icon={Plane}
          color="blue"
        />
        <StatCard
          title="Paddocks Covered"
          value={paddocksCovered}
          icon={MapPin}
          color="green"
        />
        <StatCard
          title="Latest Flight"
          value={latestFlight ? format(parseISO(latestFlight.acquisition_date), 'dd MMM yyyy') : '—'}
          subtitle={latestFlight?.source_name || null}
          icon={Plane}
          color="purple"
        />
      </div>

      {/* Flights Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : !flights?.length ? (
          <div className="text-center py-12">
            <Plane className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No drone flights logged</h3>
            <p className="text-gray-500 mt-1">Log your first drone flight to start calibrating monitoring data</p>
            <button
              onClick={() => setShowFlightModal(true)}
              className="btn bg-green-600 text-white hover:bg-green-700 mt-4 inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Log First Flight
            </button>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {flights.map(flight => (
              <FlightRow
                key={flight.id}
                flight={flight}
                ranchId={ranchId}
                paddocks={paddocks || []}
                expanded={expandedFlight === flight.id}
                onToggle={() => setExpandedFlight(expandedFlight === flight.id ? null : flight.id)}
                onAddObs={() => setShowObsModal(flight.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      {showFlightModal && (
        <LogFlightModal
          ranchId={ranchId}
          onClose={() => setShowFlightModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['droneSources'] })
            setShowFlightModal(false)
          }}
        />
      )}
      {showObsModal && (
        <AddObservationModal
          ranchId={ranchId}
          sourceId={showObsModal}
          sensorType={flights?.find(f => f.id === showObsModal)?.quality_flags?.sensor || 'multispectral'}
          paddocks={paddocks || []}
          onClose={() => setShowObsModal(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['droneObservations', showObsModal] })
            setShowObsModal(null)
          }}
        />
      )}
    </div>
  )
}

function FlightRow({ flight, ranchId, paddocks, expanded, onToggle, onAddObs }) {
  const flags = flight.quality_flags || {}
  const sensor = flags.sensor || 'multispectral'
  const isLidar = sensor === 'lidar'
  const isSpectral = sensor === 'multispectral'

  const { data: flightObs = [] } = useQuery({
    queryKey: ['droneObservations', flight.id],
    queryFn: () => getRemoteSensingObservations(ranchId, { source_id: flight.id }),
    enabled: expanded,
  })

  const paddockMap = {}
  paddocks.forEach(p => { paddockMap[p.id] = p.code })

  return (
    <div>
      <div
        className="flex items-center gap-4 px-6 py-4 hover:bg-gray-50 cursor-pointer"
        onClick={onToggle}
      >
        {expanded
          ? <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
          : <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
        }
        <div className="flex-1 grid grid-cols-5 gap-4 text-sm">
          <div className="font-medium text-gray-900">
            {format(parseISO(flight.acquisition_date), 'dd MMM yyyy')}
          </div>
          <div className="text-gray-600">
            {flags.aircraft ? AIRCRAFT[flags.aircraft] || flags.aircraft : flight.source_name || '—'}
          </div>
          <div className="text-gray-600">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              isLidar ? 'bg-purple-50 text-purple-700' :
              isSpectral ? 'bg-blue-50 text-blue-700' :
              'bg-gray-50 text-gray-700'
            }`}>
              {SENSORS[sensor] || sensor}
            </span>
          </div>
          <div className="text-gray-600">
            {flags.altitude_m ? `${flags.altitude_m}m` : '—'}
          </div>
          <div className="text-gray-600">
            {flight.resolution_m ? `${flight.resolution_m}m GSD` : '—'}
          </div>
        </div>
      </div>
      {expanded && (
        <div className="bg-gray-50 px-6 py-4 border-t">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">Paddock Observations</h4>
            <button
              onClick={(e) => { e.stopPropagation(); onAddObs() }}
              className="btn bg-white text-gray-700 border border-gray-300 hover:bg-gray-100 text-xs flex items-center gap-1"
            >
              <Plus className="h-3 w-3" />
              Add Paddock Data
            </button>
          </div>
          {flightObs.length === 0 ? (
            <p className="text-sm text-gray-400 italic">No observations recorded for this flight yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider">
                    <th className="pb-2 pr-4">Paddock</th>
                    {isLidar ? (
                      <>
                        <th className="pb-2 pr-4 text-right">Grass Height</th>
                        <th className="pb-2 pr-4 text-right">Canopy Height</th>
                        <th className="pb-2 pr-4 text-right">Tree Density</th>
                        <th className="pb-2 text-right">Biomass</th>
                      </>
                    ) : (
                      <>
                        {isSpectral && <th className="pb-2 pr-4 text-right">Veg. Score</th>}
                        <th className="pb-2 pr-4 text-right">Green %</th>
                        <th className="pb-2 pr-4 text-right">Bare %</th>
                        <th className="pb-2 text-right">Biomass</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {flightObs.map(obs => (
                    <tr key={obs.id}>
                      <td className="py-2 pr-4">
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-700">
                          {obs.paddock_code || paddockMap[obs.paddock_id] || '—'}
                        </span>
                      </td>
                      {isLidar ? (
                        <>
                          <td className="py-2 pr-4 text-right text-gray-700">
                            {obs.grass_height_mean_cm != null ? `${Number(obs.grass_height_mean_cm).toFixed(0)} cm` : '—'}
                          </td>
                          <td className="py-2 pr-4 text-right text-gray-700">
                            {obs.canopy_height_mean_m != null ? `${Number(obs.canopy_height_mean_m).toFixed(1)} m` : '—'}
                          </td>
                          <td className="py-2 pr-4 text-right text-gray-700">
                            {obs.tree_density_per_ha != null ? `${Number(obs.tree_density_per_ha).toFixed(0)}/ha` : '—'}
                          </td>
                          <td className="py-2 text-right font-medium text-gray-900">
                            {obs.pasture_biomass_kg_ha != null ? `${Number(obs.pasture_biomass_kg_ha).toLocaleString()} kg/ha` : '—'}
                          </td>
                        </>
                      ) : (
                        <>
                          {isSpectral && (
                            <td className="py-2 pr-4 text-right text-gray-700">
                              {obs.paddock_ndvi != null ? Number(obs.paddock_ndvi).toFixed(3) : '—'}
                            </td>
                          )}
                          <td className="py-2 pr-4 text-right text-gray-700">
                            {obs.pasture_cover_pct != null ? `${Number(obs.pasture_cover_pct).toFixed(1)}%` : '—'}
                          </td>
                          <td className="py-2 pr-4 text-right text-gray-700">
                            {obs.bare_ground_pct != null ? `${Number(obs.bare_ground_pct).toFixed(1)}%` : '—'}
                          </td>
                          <td className="py-2 text-right font-medium text-gray-900">
                            {obs.pasture_biomass_kg_ha != null ? `${Number(obs.pasture_biomass_kg_ha).toLocaleString()} kg/ha` : '—'}
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function LogFlightModal({ ranchId, onClose, onSuccess }) {
  const today = new Date().toISOString().split('T')[0]
  const [formData, setFormData] = useState({
    acquisition_date: today,
    aircraft: 'mavic_enterprise',
    sensor: 'multispectral',
    altitude_m: '',
    resolution_m: '',
    notes: '',
  })
  const [error, setError] = useState('')

  const mutation = useMutation({
    mutationFn: createRemoteSensingSource,
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to log flight'),
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!formData.acquisition_date) {
      setError('Select a date')
      return
    }

    const aircraftLabel = AIRCRAFT[formData.aircraft] || formData.aircraft
    const sensorLabel = SENSORS[formData.sensor] || formData.sensor

    mutation.mutate({
      ranch_id: ranchId,
      source_type: 'drone',
      source_name: `${aircraftLabel} ${sensorLabel}`,
      acquisition_date: formData.acquisition_date,
      resolution_m: formData.resolution_m ? parseFloat(formData.resolution_m) : null,
      resolution_class: 'fine',
      quality_flags: {
        aircraft: formData.aircraft,
        sensor: formData.sensor,
        altitude_m: formData.altitude_m ? parseFloat(formData.altitude_m) : null,
        notes: formData.notes || null,
      },
    })
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Log Drone Flight</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Flight Date *</label>
              <input
                type="date"
                value={formData.acquisition_date}
                onChange={e => handleChange('acquisition_date', e.target.value)}
                className="input w-full"
                required
              />
            </div>
            <div>
              <label className="label">Aircraft *</label>
              <select
                value={formData.aircraft}
                onChange={e => handleChange('aircraft', e.target.value)}
                className="input w-full"
              >
                {Object.entries(AIRCRAFT).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Sensor *</label>
              <select
                value={formData.sensor}
                onChange={e => handleChange('sensor', e.target.value)}
                className="input w-full"
              >
                {Object.entries(SENSORS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Altitude (m)</label>
              <input
                type="number"
                value={formData.altitude_m}
                onChange={e => handleChange('altitude_m', e.target.value)}
                placeholder="e.g. 60"
                className="input w-full"
                min="1"
                step="1"
              />
            </div>
          </div>

          <div>
            <label className="label">Ground Sample Distance (m)</label>
            <input
              type="number"
              value={formData.resolution_m}
              onChange={e => handleChange('resolution_m', e.target.value)}
              placeholder="e.g. 0.05"
              className="input w-full"
              min="0.001"
              step="0.001"
            />
            <p className="text-xs text-gray-400 mt-1">Resolution of the processed imagery</p>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => handleChange('notes', e.target.value)}
              placeholder="Flight conditions, purpose, area covered..."
              className="input w-full"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Plane className="h-4 w-4" />
                  Log Flight
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

function AddObservationModal({ ranchId, sourceId, sensorType, paddocks, onClose, onSuccess }) {
  const isLidar = sensorType === 'lidar'
  const isSpectral = sensorType === 'multispectral'
  const isRgb = sensorType === 'rgb'

  const [formData, setFormData] = useState({
    paddock_id: '',
    // Multispectral fields
    paddock_ndvi: '',
    // Multispectral + RGB fields
    pasture_cover_pct: '',
    bare_ground_pct: '',
    // LiDAR fields
    grass_height_mean_cm: '',
    canopy_height_mean_m: '',
    tree_density_per_ha: '',
    // Shared
    pasture_biomass_kg_ha: '',
    notes: '',
  })
  const [error, setError] = useState('')

  const today = new Date().toISOString().split('T')[0]

  const mutation = useMutation({
    mutationFn: bulkCreateObservations,
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to save observation'),
  })

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const parseNum = (val) => val ? parseFloat(val) : null

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!formData.paddock_id) {
      setError('Select a paddock')
      return
    }

    const obs = {
      paddock_id: formData.paddock_id,
      observation_date: today,
      confidence: 'high',
      // Conditionally include fields based on sensor type
      ...(isSpectral && {
        paddock_ndvi: parseNum(formData.paddock_ndvi),
      }),
      ...((isSpectral || isRgb) && {
        pasture_cover_pct: parseNum(formData.pasture_cover_pct),
        bare_ground_pct: parseNum(formData.bare_ground_pct),
      }),
      ...(isLidar && {
        grass_height_mean_cm: parseNum(formData.grass_height_mean_cm),
        canopy_height_mean_m: parseNum(formData.canopy_height_mean_m),
        tree_density_per_ha: parseNum(formData.tree_density_per_ha),
      }),
      // Biomass: multispectral (modeled) and LiDAR (volumetric)
      ...((isSpectral || isLidar) && {
        pasture_biomass_kg_ha: parseNum(formData.pasture_biomass_kg_ha),
      }),
      extra_metrics: formData.notes ? { notes: formData.notes } : null,
    }

    mutation.mutate({
      source_id: sourceId,
      ranch_id: ranchId,
      observations: [obs],
    })
  }

  const sensorLabel = SENSORS[sensorType] || sensorType

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Paddock Observation</h2>
            <span className={`mt-1 inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
              isLidar ? 'bg-purple-50 text-purple-700' :
              isSpectral ? 'bg-blue-50 text-blue-700' :
              'bg-gray-50 text-gray-700'
            }`}>
              {sensorLabel} data
            </span>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div>
            <label className="label">Paddock *</label>
            <select
              value={formData.paddock_id}
              onChange={e => handleChange('paddock_id', e.target.value)}
              className="input w-full"
              required
            >
              <option value="">Select paddock</option>
              {paddocks.map(p => (
                <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
              ))}
            </select>
          </div>

          {/* Multispectral: NDVI + cover percentages */}
          {isSpectral && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Vegetation Score</label>
                <input
                  type="number"
                  value={formData.paddock_ndvi}
                  onChange={e => handleChange('paddock_ndvi', e.target.value)}
                  placeholder="e.g. 0.45"
                  className="input w-full"
                  min="-1"
                  max="1"
                  step="0.001"
                />
              </div>
              <div>
                <label className="label">Green Cover %</label>
                <input
                  type="number"
                  value={formData.pasture_cover_pct}
                  onChange={e => handleChange('pasture_cover_pct', e.target.value)}
                  placeholder="e.g. 72"
                  className="input w-full"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>
          )}

          {/* RGB: cover percentages only (no NDVI) */}
          {isRgb && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Green Cover %</label>
                <input
                  type="number"
                  value={formData.pasture_cover_pct}
                  onChange={e => handleChange('pasture_cover_pct', e.target.value)}
                  placeholder="e.g. 72"
                  className="input w-full"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
              <div>
                <label className="label">Bare Ground %</label>
                <input
                  type="number"
                  value={formData.bare_ground_pct}
                  onChange={e => handleChange('bare_ground_pct', e.target.value)}
                  placeholder="e.g. 15"
                  className="input w-full"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
            </div>
          )}

          {/* Multispectral: bare ground in second row */}
          {isSpectral && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="label">Bare Ground %</label>
                <input
                  type="number"
                  value={formData.bare_ground_pct}
                  onChange={e => handleChange('bare_ground_pct', e.target.value)}
                  placeholder="e.g. 15"
                  className="input w-full"
                  min="0"
                  max="100"
                  step="0.1"
                />
              </div>
              <div>
                <label className="label">Biomass Estimate (kg DM/ha)</label>
                <input
                  type="number"
                  value={formData.pasture_biomass_kg_ha}
                  onChange={e => handleChange('pasture_biomass_kg_ha', e.target.value)}
                  placeholder="e.g. 2500"
                  className="input w-full"
                  min="0"
                  step="10"
                />
              </div>
            </div>
          )}

          {/* LiDAR: structural measurements */}
          {isLidar && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Grass Height (cm)</label>
                  <input
                    type="number"
                    value={formData.grass_height_mean_cm}
                    onChange={e => handleChange('grass_height_mean_cm', e.target.value)}
                    placeholder="e.g. 35"
                    className="input w-full"
                    min="0"
                    step="0.5"
                  />
                </div>
                <div>
                  <label className="label">Canopy Height (m)</label>
                  <input
                    type="number"
                    value={formData.canopy_height_mean_m}
                    onChange={e => handleChange('canopy_height_mean_m', e.target.value)}
                    placeholder="e.g. 4.5"
                    className="input w-full"
                    min="0"
                    step="0.1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Tree Density (per ha)</label>
                  <input
                    type="number"
                    value={formData.tree_density_per_ha}
                    onChange={e => handleChange('tree_density_per_ha', e.target.value)}
                    placeholder="e.g. 45"
                    className="input w-full"
                    min="0"
                    step="1"
                  />
                </div>
                <div>
                  <label className="label">Biomass Estimate (kg DM/ha)</label>
                  <input
                    type="number"
                    value={formData.pasture_biomass_kg_ha}
                    onChange={e => handleChange('pasture_biomass_kg_ha', e.target.value)}
                    placeholder="e.g. 2500"
                    className="input w-full"
                    min="0"
                    step="10"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => handleChange('notes', e.target.value)}
              placeholder="Processing notes, data quality observations..."
              className="input w-full"
              rows={2}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <MapPin className="h-4 w-4" />
                  Save Observation
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
